import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { downloadFile, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Button, Modal, Field, Input, Select, Empty, Badge } from "@/components/ui-kit";
import { Plus, Trash2, MapPin, Clock, Swords, Dumbbell, PartyPopper, Star, ClipboardCheck, UserCheck, FileText, Sheet, GraduationCap, Upload, Mail, AlertTriangle, CheckCircle2, Hourglass, List, CalendarDays, ChevronLeft, ChevronRight, Users, Eye, X, Save, Bell, FlaskConical, Pencil, Palmtree, Cake } from "lucide-react";
import { fmtDate } from "@/lib/utils";
import { toast } from "sonner";

import StageManager from "@/components/StageManager";
import SessionView from "@/components/SessionView";
import CampModal from "@/components/CampModal";
import { DirectionsButtons } from "@/components/DirectionsButtons";

const ADMIN_COLORS = { green: "#22c55e", orange: "#f59e0b", red: "#ef4444", blue: "#3b82f6" };

const TYPES = { training: { label: "Entraînement", icon: Dumbbell, color: "blue" }, match: { label: "Match", icon: Swords, color: "green" }, stage: { label: "Stage", icon: GraduationCap, color: "purple" }, event: { label: "Événement", icon: PartyPopper, color: "yellow" } };
const STATUS = {
  pending: { label: "En attente de confirmation", color: "yellow" },
  confirmed: { label: "Confirmé", color: "green" },
  cancelled: { label: "Annulé", color: "red" },
};
const PREP = {
  todo: { label: "À préparer", color: "red", icon: AlertTriangle },
  pending: { label: "En attente", color: "yellow", icon: Hourglass },
  ready: { label: "Prête", color: "green", icon: CheckCircle2 },
};
const BREAKDOWN_META = {
  declared_present: { label: "Prévus", color: "green", icon: UserCheck },
  confirmed_present: { label: "Confirmés présents", color: "green", icon: CheckCircle2 },
  pending: { label: "En attente", color: "yellow", icon: Hourglass },
  declared_absent: { label: "Absents", color: "red", icon: X },
};
const BREAKDOWN_ORDER = ["declared_present", "pending", "declared_absent", "confirmed_present"];
const prepStatus = (ev) => {
  const hasEx = (ev.exercise_ids?.length || 0) > 0;
  if (!hasEx) return "todo";
  return ev.session_ready ? "ready" : "pending";
};
const empty = { type: "training", title: "", date: "", end_date: "", time: "", location: "", location_id: "", category: "", opponent: "", description: "", event_cost: "", event_deposit: "", group: "all" };
const emptyEval = { rating: 3, note: "", result: "" };
const eventGroup = (e) => {
  const g = e.group;
  if (g === "gr1" || g === "gr2") return g;
  const t = `${e.title || ""} ${e.category || ""}`.toUpperCase().replace(/[\s.]+/g, "");
  if (/GR(OUPE)?1\b/.test(t) || t.includes("GR1")) return "gr1";
  if (/GR(OUPE)?2\b/.test(t) || t.includes("GR2")) return "gr2";
  return null;
};
const AV_STATUS = {
  available: { label: "Disponible", color: "yellow" },
  confirmed: { label: "Retenu", color: "green" },
};

const seasonOf = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const y = d.getFullYear();
  const start = d.getMonth() + 1 >= 7 ? y : y - 1;
  return `${start}-${start + 1}`;
};
const currentSeason = () => {
  const d = new Date();
  const y = d.getFullYear();
  const start = d.getMonth() + 1 >= 7 ? y : y - 1;
  return `${start}-${start + 1}`;
};

export default function Planning() {
  const { user, can } = useAuth();
  const canEdit = can("planning_manage");
  const canExport = can("data_export");
  const canImport = can("events_import");
  const isParent = user?.role === "parent";
  const [events, setEvents] = useState([]);
  const [cats, setCats] = useState([]);
  const [evals, setEvals] = useState({});
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [notifyAllEv, setNotifyAllEv] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [tab, setTab] = useState("all");
  const [grFilter, setGrFilter] = useState("all");
  const grTouched = useRef(false);
  const [season, setSeason] = useState(currentSeason());
  const [evalOpen, setEvalOpen] = useState(false);
  const [evalEvent, setEvalEvent] = useState(null);
  const [evalForm, setEvalForm] = useState(emptyEval);
  const [declOpen, setDeclOpen] = useState(false);
  const [declEvent, setDeclEvent] = useState(null);
  const [declState, setDeclState] = useState({});
  const [stageOpen, setStageOpen] = useState(false);
  const [stageEvent, setStageEvent] = useState(null);
  const [campEvent, setCampEvent] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const isAdmin = user?.role === "admin";
  const isCoach = user?.role === "coach";
  const [avOverview, setAvOverview] = useState({});
  const [locations, setLocations] = useState([]);
  const [exercisesList, setExercisesList] = useState([]);
  const [sessionEvent, setSessionEvent] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const [calMonth, setCalMonth] = useState(new Date());
  const [dayModal, setDayModal] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [showInfo, setShowInfo] = useState(false);
  const [vacModal, setVacModal] = useState(false);
  const [vacForm, setVacForm] = useState({ name: "", start: "", end: "" });
  const [vacEditing, setVacEditing] = useState(null);
  const [allVacs, setAllVacs] = useState([]);
  const [convoEvent, setConvoEvent] = useState(null);
  const [convoMembers, setConvoMembers] = useState([]);
  const [convoSelected, setConvoSelected] = useState(() => new Set());
  const [convoFilter, setConvoFilter] = useState("all");
  const [convoGr, setConvoGr] = useState("all");
  const [convoSending, setConvoSending] = useState(false);
  const [convoGroups, setConvoGroups] = useState([]);
  const [convoPreview, setConvoPreview] = useState(null); // { subject, html }
  const [convoPreviewLoading, setConvoPreviewLoading] = useState(false);
  const [convoChannels, setConvoChannels] = useState({ app: true, email: false });
  const [trackEvent, setTrackEvent] = useState(null);
  const [trackData, setTrackData] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [convoMessage, setConvoMessage] = useState("");

  const doImport = async (e) => {
    e.preventDefault();
    setImporting(true);
    try {
      const { data } = await api.post("/events/import", { sheet_url: importUrl });
      toast.success(`${data.imported} événement(s) importé(s) · ${data.skipped} ignoré(s)`);
      if (data.errors?.length) toast.warning(`${data.errors.length} ligne(s) en erreur`);
      setImportOpen(false); setImportUrl(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Import échoué"); }
    finally { setImporting(false); }
  };

  const load = () => {
    api.get("/events").then((r) => setEvents(r.data));
    api.get("/categories").then((r) => setCats(r.data));
    if (isParent) api.get("/members").then((r) => {
      setMembers(r.data);
      const gs = [...new Set((r.data || []).map((m) => m.group).filter(Boolean))];
      if (gs.length === 1 && !grTouched.current) setGrFilter(gs[0]);
    });
    if (isAdmin) api.get("/coach/availability-overview").then((r) => setAvOverview(r.data || {})).catch(() => {});
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    if (isAdmin || isCoach) api.get("/exercises").then((r) => setExercisesList(r.data)).catch(() => {});
    api.get("/evaluations").then((r) => {
      const map = {};
      r.data.forEach((e) => { map[e.event_id] = e; });
      setEvals(map);
    });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  useEffect(() => {
    api.get(`/calendar/holidays?season=${season}`).then((r) => {
      setHolidays(r.data.holidays || []);
      setVacations(r.data.vacations || []);
    }).catch(() => {});
  }, [season]);

  useEffect(() => {
    api.get("/calendar/birthdays").then((r) => setBirthdays(r.data || [])).catch(() => {});
  }, []);

  const openVacModal = async () => {
    setVacEditing(null); setVacForm({ name: "", start: "", end: "" });
    await loadAllVacs();
    setVacModal(true);
  };
  const refreshVacations = async () => {
    try {
      const { data } = await api.get(`/calendar/holidays?season=${season}`);
      setVacations(data.vacations || []);
    } catch {}
  };
  const saveVac = async (e) => {
    e.preventDefault();
    try {
      if (vacEditing) await api.put(`/school-vacations/${vacEditing}`, vacForm);
      else await api.post("/school-vacations", vacForm);
      setVacEditing(null); setVacForm({ name: "", start: "", end: "" });
      await loadAllVacs();
      await refreshVacations();
      toast.success("Enregistré");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const loadAllVacs = async () => { try { const { data } = await api.get("/school-vacations"); setAllVacs(data); } catch {} };
  const editVac = (v) => { setVacEditing(v.id); setVacForm({ name: v.name, start: v.start, end: v.end || v.start }); };
  const removeVac = async (id) => {
    if (!window.confirm("Supprimer cette période ?")) return;
    try {
      await api.delete(`/school-vacations/${id}`);
      await loadAllVacs();
      await refreshVacations();
      toast.success("Supprimé");
    } catch { toast.error("Erreur"); }
  };

  const openDeclare = async (ev) => {
    setDeclEvent(ev);
    const { data } = await api.get(`/attendance/${ev.id}`);
    const map = {};
    data.forEach((r) => { map[r.member_id] = { declared: r.parent_declared, confirmed: r.coach_confirmed, status: r.status }; });
    setDeclState(map);
    setDeclOpen(true);
  };

  const declare = async (memberId, value) => {
    try {
      await api.post("/attendance/declare", { event_id: declEvent.id, member_id: memberId, declared: value });
      setDeclState((s) => ({ ...s, [memberId]: { ...(s[memberId] || {}), declared: value } }));
      toast.success(value === "present" ? "Présence déclarée" : "Absence signalée");
    } catch { toast.error("Erreur"); }
  };

  const openNew = () => { setEditing(null); setForm(empty); setNotifyAllEv(false); setOpen(true); };
  const openEdit = (ev) => {
    let lid = ev.location_id || "";
    if (!lid && ev.location) { const m = locations.find((l) => l.name === ev.location); if (m) lid = m.id; }
    setEditing(ev); setForm({ ...empty, ...ev, location_id: lid }); setOpen(true);
  };

  const openEval = (ev) => {
    setEvalEvent(ev);
    const existing = evals[ev.id];
    setEvalForm(existing ? { rating: existing.rating, note: existing.note || "", result: existing.result || "" } : emptyEval);
    setEvalOpen(true);
  };

  const saveEval = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/events/${evalEvent.id}/evaluation`, evalForm);
      toast.success("Évaluation enregistrée"); setEvalOpen(false); load();
    } catch { toast.error("Erreur"); }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const cost = form.event_cost === "" || form.event_cost == null ? null : parseFloat(form.event_cost);
      const deposit = form.event_deposit === "" || form.event_deposit == null ? null : parseFloat(form.event_deposit);
      const payload = { ...form, event_cost: cost, event_deposit: deposit, paid: (cost || 0) > 0 };
      if (editing) { await api.put(`/events/${editing.id}`, payload); }
      else {
        await api.post("/events", payload);
        if (notifyAllEv && user?.role === "admin") {
          try {
            const parts = [form.time ? `à ${form.time}` : "", form.location ? `· ${form.location}` : "", form.opponent ? `· vs ${form.opponent}` : ""].filter(Boolean).join(" ");
            const txt = `📅 ${form.title}\n${fmtDate(form.date)} ${parts}`.trim();
            const { data } = await api.post("/messages/broadcast", { scope: "all", title: "Nouvel événement", text: txt });
            toast.success(`Diffusé dans la bulle de ${data.sent} membre(s)`);
          } catch { toast.error("Événement créé, mais diffusion bulle échouée."); }
        }
      }
      if (form.date) setSeason(seasonOf(form.date));
      toast.success("Enregistré"); setOpen(false); load();
    } catch { toast.error("Erreur"); }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ?")) return; await api.delete(`/events/${id}`); load(); };

  const setStatus = async (ev, status) => {
    try {
      const { data } = await api.post(`/events/${ev.id}/status`, { status });
      if (status === "cancelled") toast.success(`Événement annulé — ${data.notified || 0} parent(s) notifié(s) par email`);
      else if (status === "confirmed") toast.success("Événement confirmé");
      else toast.success("Remis en attente");
      load();
    } catch { toast.error("Erreur"); }
  };

  const convoke = async (ev) => {
    setConvoEvent(ev);
    const isConv = ev.type === "match" || ev.type === "event";
    setConvoFilter(isConv ? "all" : (ev.category || "all"));
    setConvoGr(!isConv && (ev.group === "gr1" || ev.group === "gr2") ? ev.group : "all");
    setConvoSelected(new Set());
    setConvoMembers([]);
    setConvoPreview(null);
    setConvoChannels({ app: true, email: false });
    try {
      const [mres, gres, tres] = await Promise.all([
        api.get("/members?status=season"),
        api.get("/convocation-groups").catch(() => ({ data: [] })),
        api.get("/settings/convocation-template").catch(() => ({ data: { text: "" } })),
      ]);
      setConvoMembers(mres.data);
      setConvoGroups(gres.data || []);
      setConvoMessage(tres.data?.text || "");
    } catch { toast.error("Impossible de charger les affiliés"); }
  };

  const convoFiltered = () => {
    const isConv = convoEvent?.type === "match" || convoEvent?.type === "event";
    const eg = isConv ? null : convoEvent?.group;
    return convoMembers.filter((m) => (convoFilter === "all" || m.category === convoFilter)
      && (!(eg === "gr1" || eg === "gr2") || m.group === eg)
      && (convoGr === "all" || m.group === convoGr));
  };

  const toggleConvoMember = (id) => setConvoSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const toggleConvoAll = () => {
    const shown = convoFiltered().map((m) => m.id);
    const allSelected = shown.length > 0 && shown.every((id) => convoSelected.has(id));
    setConvoSelected((s) => {
      const n = new Set(s);
      if (allSelected) shown.forEach((id) => n.delete(id));
      else shown.forEach((id) => n.add(id));
      return n;
    });
  };

  const applyGroup = (g) => {
    const valid = new Set(convoMembers.map((m) => m.id));
    setConvoSelected((s) => {
      const n = new Set(s);
      (g.member_ids || []).forEach((id) => { if (valid.has(id)) n.add(id); });
      return n;
    });
    toast.success(`Groupe « ${g.name} » appliqué`);
  };

  const saveGroup = async () => {
    if (!convoSelected.size) { toast.error("Sélectionnez d'abord des affiliés"); return; }
    const name = window.prompt("Nom du groupe (ex. Équipe A) :");
    if (!name || !name.trim()) return;
    try {
      const { data } = await api.post("/convocation-groups", { name: name.trim(), member_ids: Array.from(convoSelected) });
      setConvoGroups((gs) => [...gs, data].sort((a, b) => a.name.localeCompare(b.name)));
      toast.success("Groupe enregistré");
    } catch { toast.error("Erreur lors de l'enregistrement du groupe"); }
  };

  const deleteGroup = async (g) => {
    if (!window.confirm(`Supprimer le groupe « ${g.name} » ?`)) return;
    try {
      await api.delete(`/convocation-groups/${g.id}`);
      setConvoGroups((gs) => gs.filter((x) => x.id !== g.id));
      toast.success("Groupe supprimé");
    } catch { toast.error("Erreur"); }
  };

  const openConvoPreview = async () => {
    setConvoPreviewLoading(true);
    try {
      const first = Array.from(convoSelected)[0];
      const params = new URLSearchParams();
      if (first) params.set("member_id", first);
      if (convoMessage) params.set("message", convoMessage);
      const { data } = await api.get(`/reminders/event/${convoEvent.id}/preview?${params.toString()}`);
      setConvoPreview(data);
    } catch { toast.error("Aperçu indisponible"); }
    finally { setConvoPreviewLoading(false); }
  };

  const saveConvoTemplate = async () => {
    try { await api.put("/settings/convocation-template", { text: convoMessage }); toast.success("Modèle enregistré par défaut"); }
    catch { toast.error("Erreur lors de l'enregistrement du modèle"); }
  };

  const sendConvoke = async () => {
    if (!convoSelected.size) { toast.error("Sélectionnez au moins un affilié"); return; }
    const channels = Object.entries(convoChannels).filter(([, v]) => v).map(([k]) => k);
    if (!channels.length) { toast.error("Choisissez au moins un canal (application ou email)"); return; }
    setConvoSending(true);
    try {
      const { data } = await api.post(`/reminders/event/${convoEvent.id}`, { member_ids: Array.from(convoSelected), channels, message: convoMessage });
      toast.success(`${data.sent} convocation(s) envoyée(s)`);
      setConvoEvent(null);
    } catch { toast.error("Erreur lors de l'envoi des convocations"); }
    finally { setConvoSending(false); }
  };

  const openTracking = async (ev) => {
    setTrackEvent(ev); setTrackData(null);
    try { const { data } = await api.get(`/events/${ev.id}/convocations`); setTrackData(data); }
    catch { toast.error("Suivi indisponible"); }
  };

  const openBreakdown = async (ev, cat) => {
    setBreakdown({ ev, cat, data: null });
    try { const { data } = await api.get(`/events/${ev.id}/attendance-breakdown`); setBreakdown({ ev, cat, data }); }
    catch { toast.error("Détail indisponible"); setBreakdown(null); }
  };

  const setDeclare = async (memberId, declared) => {
    if (!breakdown) return;
    const ev = breakdown.ev;
    try {
      await api.post("/attendance/declare", { event_id: ev.id, member_id: memberId, declared });
      const { data } = await api.get(`/events/${ev.id}/attendance-breakdown`);
      setBreakdown((b) => (b ? { ...b, data } : b));
      load();
      toast.success("Statut mis à jour");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const sendResult = async (ev) => {
    try {
      const { data } = await api.post(`/events/${ev.id}/send-result`);
      toast.success(`Résultat ${data.result || ""} envoyé à ${data.sent} destinataire(s)`);
    } catch (err) { toast.error(err.response?.data?.detail || "Envoi du résultat échoué"); }
  };

  const seasons = (() => {
    const set = new Set(events.map((e) => seasonOf(e.date)).filter(Boolean));
    set.add(currentSeason());
    return Array.from(set).sort().reverse();
  })();
  const filtered = (() => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const keyOf = (e) => `${e.date || ""}T${e.time || "00:00"}`;
    const endOf = (e) => e.end_date || e.date || "";
    return events
      .filter((e) => seasonOf(e.date) === season)
      .filter((e) => tab === "all" || e.type === tab)
      .filter((e) => grFilter === "all" || eventGroup(e) === grFilter)
      .slice()
      .sort((a, b) => {
        const aPast = endOf(a) < todayStr;
        const bPast = endOf(b) < todayStr;
        if (aPast !== bPast) return aPast ? 1 : -1; // les événements passés en dernier
        const cmp = keyOf(a).localeCompare(keyOf(b));
        return aPast ? -cmp : cmp; // à venir : ordre croissant ; passés : plus récent d'abord
      });
  })();

  return (
    <div>
      <PageHeader title="Planning" subtitle={`Saison ${season} — entraînements, matchs et événements du club.`}
        action={(canEdit || canExport || canImport) && (
          <div className="flex gap-2 flex-wrap">
            {canExport && <Button data-testid="export-planning-pdf" variant="outline" onClick={() => downloadFile(`/export/planning.pdf?season=${season}${grFilter !== "all" ? `&group=${grFilter}` : ""}`, `planning_${season}${grFilter !== "all" ? `_${grFilter}` : ""}.pdf`).catch(() => toast.error("Export échoué"))}><FileText size={16} /> PDF</Button>}
            {canExport && <Button data-testid="export-planning-xlsx" variant="outline" onClick={() => downloadFile(`/export/planning.xlsx?season=${season}${grFilter !== "all" ? `&group=${grFilter}` : ""}`, `planning_${season}${grFilter !== "all" ? `_${grFilter}` : ""}.xlsx`).catch(() => toast.error("Export échoué"))}><Sheet size={16} /> Excel</Button>}
            {canImport && <Button data-testid="import-planning-btn" variant="outline" onClick={() => setImportOpen(true)}><Upload size={16} /> Importer</Button>}
            {isAdmin && <Button data-testid="manage-vacations-btn" variant="outline" onClick={openVacModal}><Palmtree size={16} /> Vacances scolaires</Button>}
            {canEdit && <Button data-testid="add-event-btn" onClick={openNew}><Plus size={16} /> Ajouter</Button>}
          </div>
        )} />

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Saison</label>
        <div className="w-52">
          <Select data-testid="season-select" value={season} onChange={(e) => setSeason(e.target.value)}>
            {seasons.map((s) => <option key={s} value={s}>Saison {s}</option>)}
          </Select>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {["all", "training", "match", "stage", "event"].map((t) => (
            <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide transition-colors ${tab === t ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              {t === "all" ? "Tout" : TYPES[t].label}
            </button>
          ))}
          <span className="w-px h-6 bg-border self-center mx-1" />
          {[["all", "Tous groupes"], ["gr1", "GR 1"], ["gr2", "GR 2"]].map(([k, lbl]) => (
            <button key={k} data-testid={`gr-filter-${k}`} onClick={() => { grTouched.current = true; setGrFilter(k); }}
              className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide transition-colors ${grFilter === k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              {lbl}
            </button>
          ))}
        </div>
        <div className="flex items-center rounded-full bg-secondary p-1" data-testid="view-toggle">
          <button data-testid="view-list-btn" onClick={() => setViewMode("list")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${viewMode === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            <List size={14} /> Liste
          </button>
          <button data-testid="view-calendar-btn" onClick={() => setViewMode("calendar")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${viewMode === "calendar" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            <CalendarDays size={14} /> Calendrier
          </button>
        </div>
      </div>

      {viewMode === "list" ? (filtered.length ? (
        <div className="space-y-3">
          {filtered.map((ev) => {
            const T = TYPES[ev.type] || TYPES.event;
            return (
              <Card key={ev.id} data-testid="event-card" className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 hover:border-primary/40 transition-colors">
                <div className="flex items-start md:items-center gap-3 md:gap-4 min-w-0 md:flex-1">
                  {ev.type === "stage" && (ev.camp_photo || ev.image)
                    ? <img src={fileUrl(ev.camp_photo || ev.image)} alt="" data-testid={`camp-thumb-${ev.id}`} className="w-11 h-11 shrink-0 rounded-lg object-cover" />
                    : <div className={`w-11 h-11 shrink-0 rounded-lg bg-primary/15 flex items-center justify-center text-primary`}><T.icon size={20} /></div>}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`font-bold break-words ${ev.status === "cancelled" ? "line-through text-muted-foreground" : ""}`}>{ev.title}</p>
                      <Badge color={T.color}>{T.label}</Badge>
                      {ev.category && <Badge color="gray">{ev.category}</Badge>}
                      {eventGroup(ev) && <Badge data-testid={`event-group-badge-${ev.id}`} color={eventGroup(ev) === "gr1" ? "green" : "blue"}>{eventGroup(ev) === "gr1" ? "GR 1" : "GR 2"}</Badge>}
                      <Badge color={(STATUS[ev.status] || STATUS.pending).color}>{(STATUS[ev.status] || STATUS.pending).label}</Badge>
                      {ev.type === "training" && isAdmin && (() => { const p = PREP[prepStatus(ev)]; return <Badge data-testid={`prep-status-${ev.id}`} color={p.color}><p.icon size={12} /> {p.label}</Badge>; })()}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-x-3 gap-y-1 flex-wrap">
                      <span className="whitespace-nowrap">{ev.type === "stage" && ev.end_date ? `du ${fmtDate(ev.date)} au ${fmtDate(ev.end_date)}` : fmtDate(ev.date)}</span>
                      {ev.time && <span className="flex items-center gap-1"><Clock size={12} /> {ev.time}</span>}
                      {ev.opponent && <span>vs {ev.opponent}</span>}
                    </p>
                    {ev.location && (() => { const addr = locations.find((l) => l.id === ev.location_id)?.address; return (
                      <div className="mt-1.5 flex items-start gap-x-3 gap-y-1.5 flex-wrap text-xs text-muted-foreground">
                        <span className="flex items-start gap-1"><MapPin size={12} className="text-primary mt-0.5 shrink-0" /> <span>{ev.location}{addr ? ` — ${addr}` : ""}</span></span>
                        <DirectionsButtons destination={addr || ev.location} />
                      </div>
                    ); })()}
                    {evals[ev.id] && (
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="flex items-center gap-0.5">
                          {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={13} className={n <= evals[ev.id].rating ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground/40"} />)}
                        </span>
                        {evals[ev.id].result && <Badge color="green">{evals[ev.id].result}</Badge>}
                        {evals[ev.id].note && <span className="text-xs text-muted-foreground italic truncate max-w-[220px]">"{evals[ev.id].note}"</span>}
                      </div>
                    )}
                    {isAdmin && (() => {
                      const avail = (avOverview[ev.id] || []).filter((c) => c.status === "available" || c.status === "confirmed");
                      if (!avail.length) return null;
                      return (
                        <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid={`event-coaches-${ev.id}`}>
                          <span className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1"><UserCheck size={12} /> Coachs dispo :</span>
                          {avail.map((c) => (
                            <Badge key={c.coach_user_id} color={AV_STATUS[c.status]?.color || "gray"}>{c.coach_name}{c.status === "confirmed" ? " · retenu" : ""}</Badge>
                          ))}
                        </div>
                      );
                    })()}
                    {(isAdmin || isCoach) && ev.counts && (
                      <div className="mt-2 flex items-center gap-1.5 flex-wrap text-xs" data-testid={`event-counts-${ev.id}`}>
                        <button type="button" data-testid={`event-see-list-${ev.id}`} onClick={() => openBreakdown(ev, "declared_present")} title="Voir la liste des joueurs prévus" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/15 text-primary font-semibold whitespace-nowrap hover:bg-primary/25 transition-colors cursor-pointer"><UserCheck size={12} /> {ev.counts.declared_present} prévu{ev.counts.declared_present > 1 ? "s" : ""}</button>
                        {ev.counts.declared_absent > 0 && <button type="button" data-testid={`event-absent-list-${ev.id}`} onClick={() => openBreakdown(ev, "declared_absent")} title="Voir la liste des joueurs absents" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 text-red-500 font-semibold whitespace-nowrap hover:bg-red-500/25 transition-colors cursor-pointer">{ev.counts.declared_absent} absent{ev.counts.declared_absent > 1 ? "s" : ""}</button>}
                        {ev.counts.pending > 0 && <button type="button" data-testid={`event-pending-list-${ev.id}`} onClick={() => openBreakdown(ev, "pending")} title="Voir la liste des joueurs en attente" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary text-muted-foreground whitespace-nowrap hover:bg-secondary/70 hover:text-foreground transition-colors cursor-pointer">{ev.counts.pending} en attente</button>}
                        {ev.counts.confirmed_present > 0 && <button type="button" data-testid={`event-confirmed-list-${ev.id}`} onClick={() => openBreakdown(ev, "confirmed_present")} title="Voir la liste des joueurs confirmés présents" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 font-semibold whitespace-nowrap hover:bg-emerald-500/25 transition-colors cursor-pointer"><CheckCircle2 size={12} /> {ev.counts.confirmed_present} confirmé{ev.counts.confirmed_present > 1 ? "s" : ""}</button>}
                        {isAdmin && ev.counts.essai_total > 0 && <span data-testid={`event-essai-${ev.id}`} title="Enfants en essai (présents / total concernés)" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-400 font-semibold whitespace-nowrap"><FlaskConical size={12} /> {ev.counts.essai_present}/{ev.counts.essai_total} en essai</span>}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 md:max-w-[58%] md:justify-end w-full md:w-auto border-t md:border-t-0 border-border pt-3 md:pt-0">
                  {canExport && (isAdmin || isCoach) && (
                    <button data-testid={`attendance-pdf-${ev.id}`} onClick={() => downloadFile(`/export/attendance/${ev.id}.pdf${grFilter !== "all" ? `?group=${grFilter}` : ""}`, `presence_${ev.date || ""}.pdf`).catch(() => toast.error("Export échoué"))}
                      className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-secondary text-muted-foreground hover:text-primary text-xs font-bold transition-colors">
                      <FileText size={14} /> Présences PDF
                    </button>
                  )}
                  {ev.type === "training" && (isAdmin || isCoach) && (
                    <button data-testid={`view-session-btn-${ev.id}`} onClick={() => setSessionEvent(ev)}
                      className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-primary/15 text-primary text-xs font-bold hover:bg-primary/25 transition-colors">
                      <Dumbbell size={14} /> Voir la séance
                    </button>
                  )}
                  {ev.type === "stage" && (
                    <button data-testid={`view-camp-btn-${ev.id}`} onClick={() => setCampEvent(ev)}
                      className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-purple-500/15 text-purple-400 text-xs font-bold hover:bg-purple-500/25 transition-colors">
                      <GraduationCap size={14} /> Stage &amp; inscription
                    </button>
                  )}
                  {canEdit && (
                    <>
                      {ev.status !== "confirmed" && <button data-testid="confirm-event-btn" onClick={() => setStatus(ev, "confirmed")} className="h-9 px-3 rounded-lg bg-secondary text-muted-foreground hover:text-primary text-xs font-bold transition-colors">Confirmer</button>}
                      {ev.status !== "cancelled" && <button data-testid="cancel-event-btn" onClick={() => setStatus(ev, "cancelled")} className="h-9 px-3 rounded-lg bg-secondary text-muted-foreground hover:text-destructive text-xs font-bold transition-colors">Annuler</button>}
                      {ev.status === "cancelled" && <button data-testid="reopen-event-btn" onClick={() => setStatus(ev, "pending")} className="h-9 px-3 rounded-lg bg-secondary text-muted-foreground hover:text-yellow-500 text-xs font-bold transition-colors">Rouvrir</button>}
                      <button data-testid="evaluate-btn" onClick={() => openEval(ev)} className="h-9 px-3 rounded-lg bg-secondary text-muted-foreground hover:text-yellow-500 text-xs font-bold flex items-center gap-1 transition-colors"><ClipboardCheck size={14} /> Évaluer</button>
                      {ev.type === "stage" && <button data-testid="manage-stage-btn" onClick={() => { setStageEvent(ev); setStageOpen(true); }} className="h-9 px-3 rounded-lg bg-secondary text-muted-foreground hover:text-purple-400 text-xs font-bold transition-colors">Gérer le stage</button>}
                      <button data-testid="convoke-btn" onClick={() => convoke(ev)} className="h-9 px-3 rounded-lg bg-secondary text-muted-foreground hover:text-primary text-xs font-bold transition-colors">Convoquer</button>
                      <button data-testid="track-convocations-btn" onClick={() => openTracking(ev)} className="h-9 px-3 rounded-lg bg-secondary text-muted-foreground hover:text-primary text-xs font-bold flex items-center gap-1 transition-colors"><ClipboardCheck size={14} /> Suivi</button>
                      {ev.type === "match" && <button data-testid="send-result-btn" onClick={() => sendResult(ev)} className="h-9 px-3 rounded-lg bg-secondary text-muted-foreground hover:text-primary text-xs font-bold flex items-center gap-1 transition-colors"><Mail size={14} /> Résultat</button>}
                      <button onClick={() => openEdit(ev)} className="h-9 px-3 rounded-lg bg-secondary text-muted-foreground hover:text-primary text-xs font-bold transition-colors">Modifier</button>
                      <button data-testid="delete-event-btn" onClick={() => remove(ev.id)} className="h-9 w-9 flex items-center justify-center rounded-lg bg-secondary text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={15} /></button>
                    </>
                  )}
                  {isParent && ev.status !== "cancelled" && (
                    <button data-testid="declare-presence-btn" onClick={() => openDeclare(ev)}
                      className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-primary/15 text-primary text-xs font-bold hover:bg-primary/25 transition-colors">
                      <UserCheck size={14} /> Présence enfant
                    </button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      ) : <Empty text={`Aucun événement pour la saison ${season}.`} />
      ) : (
        <>
          {(holidays.length > 0 || vacations.length > 0) && (
            <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/5" data-testid="holidays-banner">
              <button type="button" data-testid="holidays-banner-toggle" onClick={() => setShowInfo((v) => !v)}
                className="w-full flex items-center gap-2 px-4 py-3 text-left">
                <Palmtree size={16} className="text-amber-500 shrink-0" />
                <span className="text-sm font-bold text-amber-500">Jours fériés & vacances scolaires</span>
                <span className="text-xs text-muted-foreground">({holidays.length} férié{holidays.length > 1 ? "s" : ""} · {vacations.length} vacance{vacations.length > 1 ? "s" : ""})</span>
                <span className="ml-auto text-xs text-muted-foreground font-semibold">{showInfo ? "Masquer" : "Afficher"}</span>
                <ChevronRight size={15} className={`text-muted-foreground transition-transform ${showInfo ? "rotate-90" : ""}`} />
              </button>
              {showInfo && (
                <div className="px-4 pb-4 grid sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="holidays-banner-content">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Vacances scolaires</p>
                    {vacations.length ? (
                      <div className="space-y-1.5">
                        {vacations.map((v) => (
                          <div key={v.id} data-testid={`vac-row-${v.id}`} className="flex items-center gap-2 text-sm">
                            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-500/60 shrink-0" />
                            <span className="font-semibold">{v.name}</span>
                            <span className="text-xs text-muted-foreground">{fmtDate(v.start)}{v.end && v.end !== v.start ? ` → ${fmtDate(v.end)}` : ""}</span>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-xs text-muted-foreground">Aucune vacance cette saison.</p>}
                  </div>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Jours fériés légaux (Belgique)</p>
                    {holidays.length ? (
                      <div className="space-y-1.5">
                        {holidays.map((h) => (
                          <div key={h.date} className="flex items-center gap-2 text-sm">
                            <span className="inline-block w-2.5 h-2.5 rounded-full bg-rose-500/70 shrink-0" />
                            <span className="font-semibold">{h.name}</span>
                            <span className="text-xs text-muted-foreground">{fmtDate(h.date)}</span>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-xs text-muted-foreground">Aucun jour férié cette saison.</p>}
                  </div>
                </div>
              )}
            </div>
          )}
          <CalendarView month={calMonth} setMonth={setCalMonth} events={filtered} holidays={holidays} vacations={vacations} birthdays={birthdays} onDayClick={(d, evs) => setDayModal({ date: d, events: evs })} />
        </>
      )}

      {sessionEvent && (
        <Modal open onClose={() => setSessionEvent(null)} title={`Séance — ${sessionEvent.title || fmtDate(sessionEvent.date)}`}>
          <p className="text-xs text-muted-foreground mb-3 flex items-center gap-2 flex-wrap">
            <Badge color="blue">{fmtDate(sessionEvent.date)}</Badge>
            {sessionEvent.time && <span className="flex items-center gap-1"><Clock size={12} /> {sessionEvent.time}</span>}
            {sessionEvent.category && <span>· {sessionEvent.category}</span>}
          </p>
          <SessionView event={sessionEvent} exercises={exercisesList} canPdf={isAdmin || isCoach} />
        </Modal>
      )}

      {dayModal && (
        <Modal open onClose={() => setDayModal(null)} title={fmtDate(dayModal.date)}>
          <div className="space-y-3" data-testid="calendar-day-detail">
            {dayModal.events.map((ev) => {
              const T = TYPES[ev.type] || TYPES.event;
              const addr = locations.find((l) => l.id === ev.location_id)?.address;
              return (
                <div key={ev.id} data-testid="calendar-day-event" className="p-3 rounded-lg bg-secondary/40 border border-border">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="w-7 h-7 rounded-lg bg-primary/15 flex items-center justify-center text-primary shrink-0"><T.icon size={15} /></span>
                    <p className={`font-bold ${ev.status === "cancelled" ? "line-through text-muted-foreground" : ""}`}>{ev.title}</p>
                    <Badge color={T.color}>{T.label}</Badge>
                    {ev.category && <Badge color="gray">{ev.category}</Badge>}
                    {canEdit && ev.type !== "holiday" && (
                      <button data-testid={`day-edit-${ev.id}`} onClick={() => { setDayModal(null); openEdit(ev); }}
                        className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-primary/15 text-primary text-xs font-bold hover:bg-primary/25 transition-colors"><Pencil size={13} /> Modifier</button>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground space-y-1 pl-9">
                    <p className="flex items-center gap-2"><Clock size={13} className="text-primary" /> {ev.time || "Heure à confirmer"}</p>
                    {(ev.location || addr) && (
                      <div className="flex items-start gap-2"><MapPin size={13} className="text-primary mt-0.5 shrink-0" />
                        <span>{ev.location}{addr ? <span className="block text-xs">{addr}</span> : null}
                          <DirectionsButtons destination={addr || ev.location} className="mt-1.5" />
                        </span>
                      </div>
                    )}
                    {ev.opponent && <p>vs {ev.opponent}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </Modal>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Modifier" : "Nouvel événement"}>
        <form onSubmit={save}>
          <Field label="Type">
            <Select data-testid="event-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              <option value="training">Entraînement</option><option value="match">Match</option><option value="stage">Stage</option><option value="event">Événement</option>
            </Select>
          </Field>
          <Field label="Titre"><Input data-testid="event-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={form.type === "stage" ? "Début (lundi)" : "Date"}><Input data-testid="event-date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
            {form.type === "stage"
              ? <Field label="Fin (vendredi)"><Input data-testid="event-end-date" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
              : <Field label="Heure"><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></Field>}
          </div>
          <Field label="Lieu">
            <Select data-testid="event-location" value={form.location_id || ""} onChange={(e) => {
              const loc = locations.find((l) => l.id === e.target.value);
              setForm({ ...form, location_id: e.target.value, location: loc ? loc.name : "" });
            }}>
              <option value="">{form.location && !form.location_id ? `${form.location} (actuel)` : "— Sélectionner un lieu —"}</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}{l.address ? ` — ${l.address}` : ""}</option>)}
            </Select>
            {isAdmin && <Link to="/lieux" className="text-xs text-primary hover:underline mt-1 inline-block">Gérer les lieux →</Link>}
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Catégorie">
              <Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                <option value="">Toutes</option>{cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </Select>
            </Field>
            {form.type === "match" && <Field label="Adversaire"><Input value={form.opponent} onChange={(e) => setForm({ ...form, opponent: e.target.value })} /></Field>}
          </div>
          <Field label="Groupe concerné">
            <Select data-testid="event-group" value={form.group || "all"} onChange={(e) => setForm({ ...form, group: e.target.value })}>
              <option value="all">Tous les groupes</option>
              <option value="gr1">GR 1</option>
              <option value="gr2">GR 2</option>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Coût / enfant (€) — laisser vide si gratuit">
              <Input data-testid="event-cost" type="number" step="0.01" min="0" value={form.event_cost ?? ""}
                onChange={(e) => setForm({ ...form, event_cost: e.target.value })} placeholder="Ex. 120" />
            </Field>
            <Field label="Acompte (€)">
              <Input data-testid="event-deposit" type="number" step="0.01" min="0" value={form.event_deposit ?? ""}
                onChange={(e) => setForm({ ...form, event_deposit: e.target.value })} placeholder="Ex. 40" />
            </Field>
          </div>
          <p className="text-xs text-muted-foreground -mt-1">Un coût rend l'événement « payant » : il apparaîtra dans <b>Financier</b> pour chaque enfant inscrit.</p>
          {!editing && user?.role === "admin" && (
            <label data-testid="event-notify-all" className="flex items-start gap-2 rounded-lg bg-secondary/40 p-3 cursor-pointer">
              <input type="checkbox" className="w-4 h-4 mt-0.5 accent-[color:var(--primary)]"
                checked={notifyAllEv} onChange={(e) => setNotifyAllEv(e.target.checked)} />
              <span className="text-xs">
                <b>Envoyer à tous les membres (bulle)</b>
                <span className="block text-muted-foreground">Prévient chaque membre dans le chat interne + une notification.</span>
              </span>
            </label>
          )}
          <Button data-testid="save-event-btn" className="w-full mt-2">Enregistrer</Button>
        </form>
      </Modal>

      <Modal open={evalOpen} onClose={() => setEvalOpen(false)} title={`Évaluation — ${evalEvent?.title || ""}`}>
        <form onSubmit={saveEval}>
          <Field label="Note globale">
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" data-testid={`eval-star-${n}`} onClick={() => setEvalForm({ ...evalForm, rating: n })}
                  className="transition-transform hover:scale-110">
                  <Star size={30} className={n <= evalForm.rating ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground/40"} />
                </button>
              ))}
            </div>
          </Field>
          {evalEvent?.type === "match" && (
            <Field label="Résultat (score)"><Input data-testid="eval-result" value={evalForm.result} onChange={(e) => setEvalForm({ ...evalForm, result: e.target.value })} placeholder="Ex : 3-2" /></Field>
          )}
          <Field label="Commentaire du coach">
            <textarea data-testid="eval-note" value={evalForm.note} onChange={(e) => setEvalForm({ ...evalForm, note: e.target.value })} rows={4}
              className="w-full px-4 py-3 rounded-lg bg-secondary border border-border text-foreground text-sm outline-none focus:border-primary resize-none"
              placeholder="Points forts, axes de progression, attitude du groupe..." />
          </Field>
          <Button data-testid="save-eval-btn" className="w-full mt-2">Enregistrer l'évaluation</Button>
        </form>
      </Modal>

      <Modal open={declOpen} onClose={() => setDeclOpen(false)} title={`Présence — ${declEvent?.title || ""}`}>
        <p className="text-sm text-muted-foreground mb-4">Indiquez si votre enfant sera présent. Le coach confirmera la présence réelle au début de la séance.</p>
        {(() => {
          const isConv = declEvent?.type === "match" || declEvent?.type === "event";
          const eg = declEvent?.group;
          let kids = !isConv && declEvent?.category ? members.filter((m) => m.category === declEvent.category) : members;
          if (!isConv && (eg === "gr1" || eg === "gr2")) kids = kids.filter((m) => m.group === eg);
          if (!kids.length) return <Empty text="Aucun enfant concerné par cet événement." />;
          return (
            <div className="space-y-3">
              {kids.map((m) => {
                const st = declState[m.id] || {};
                return (
                  <div key={m.id} data-testid="declare-row" className="p-3 rounded-lg bg-secondary/50 border border-border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-sm">{m.first_name} {m.last_name}</span>
                      {st.confirmed && <Badge color="green">Confirmé par le coach{st.status && st.status !== "present" ? ` (${st.status})` : ""}</Badge>}
                    </div>
                    <div className="flex gap-2">
                      <button data-testid={`declare-present-${m.id}`} onClick={() => declare(m.id, "present")}
                        className={`flex-1 py-2 rounded-lg text-xs font-bold transition-colors ${st.declared === "present" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                        Sera présent
                      </button>
                      <button data-testid={`declare-absent-${m.id}`} onClick={() => declare(m.id, "absent")}
                        className={`flex-1 py-2 rounded-lg text-xs font-bold transition-colors ${st.declared === "absent" ? "bg-destructive text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                        Absent
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </Modal>

      <Modal open={!!convoEvent} onClose={() => setConvoEvent(null)} title={`Convocation — ${convoEvent?.title || ""}`}>
        {convoEvent && (() => {
          const shown = convoFiltered();
          const allSelected = shown.length > 0 && shown.every((m) => convoSelected.has(m.id));
          return (
            <div>
              <p className="text-sm text-muted-foreground mb-4">
                Sélectionnez les affiliés de la saison en cours à convoquer. Ils recevront la convocation dans l'application (et/ou par email) et pourront confirmer leur présence depuis leur tableau de bord.
              </p>
              <div className="mb-4">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Users size={13} /> Groupes</span>
                  <button type="button" data-testid="convo-save-group" onClick={saveGroup}
                    className="text-xs font-bold text-primary hover:underline flex items-center gap-1 disabled:opacity-40" disabled={!convoSelected.size}>
                    <Save size={13} /> Enregistrer la sélection
                  </button>
                </div>
                {convoGroups.length ? (
                  <div className="flex flex-wrap gap-2" data-testid="convo-groups">
                    {convoGroups.map((g) => (
                      <span key={g.id} data-testid={`convo-group-${g.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-secondary border border-border pl-3 pr-1.5 py-1 text-xs">
                        <button type="button" onClick={() => applyGroup(g)} className="font-semibold hover:text-primary transition-colors">
                          {g.name} <span className="text-muted-foreground">({(g.member_ids || []).length})</span>
                        </button>
                        <button type="button" data-testid={`convo-group-del-${g.id}`} onClick={() => deleteGroup(g)}
                          className="text-muted-foreground hover:text-destructive"><X size={13} /></button>
                      </span>
                    ))}
                  </div>
                ) : <p className="text-xs text-muted-foreground">Aucun groupe. Sélectionnez des affiliés puis « Enregistrer la sélection » pour créer un groupe réutilisable (ex. Équipe A).</p>}
              </div>
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <div className="w-48">
                  <Select data-testid="convo-category-filter" value={convoFilter} onChange={(e) => setConvoFilter(e.target.value)}>
                    <option value="all">Toutes les catégories</option>
                    {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </Select>
                </div>
                <div className="flex gap-1.5" data-testid="convo-group-filter">
                  {[["all", "Tous"], ["gr1", "GR 1"], ["gr2", "GR 2"]].map(([k, lbl]) => (
                    <button key={k} type="button" data-testid={`convo-gr-${k}`} onClick={() => setConvoGr(k)}
                      className={`px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-colors ${convoGr === k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{lbl}</button>
                  ))}
                </div>
                <Button type="button" data-testid="convo-select-all" variant="outline" onClick={toggleConvoAll} disabled={!shown.length}>
                  {allSelected ? "Tout désélectionner" : "Tout sélectionner"}
                </Button>
                <span className="text-xs text-muted-foreground ml-auto" data-testid="convo-count">
                  {convoSelected.size} sélectionné(s) · {shown.length} affiché(s)
                </span>
              </div>
              {shown.length ? (
                <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
                  {shown.map((m) => {
                    const checked = convoSelected.has(m.id);
                    return (
                      <button key={m.id} type="button" data-testid={`convo-member-${m.id}`} onClick={() => toggleConvoMember(m.id)}
                        className={`w-full flex items-center justify-between gap-3 p-3 rounded-lg border text-left transition-colors ${checked ? "border-primary bg-primary/10" : "border-border bg-secondary/40 hover:border-primary/50"}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <span className={`w-5 h-5 rounded flex items-center justify-center shrink-0 border ${checked ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/40"}`}>
                            {checked && <CheckCircle2 size={14} />}
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold truncate flex items-center gap-1.5" style={m.admin_color ? { color: ADMIN_COLORS[m.admin_color] } : undefined}>
                              {m.admin_color && <span data-testid={`convo-color-${m.id}`} className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: ADMIN_COLORS[m.admin_color] }} />}
                              {m.first_name} {m.last_name}
                            </p>
                            {m.category && <p className="text-[11px] text-muted-foreground">{m.category}</p>}
                          </div>
                        </div>
                        {m.category && <Badge color="blue">{m.category}</Badge>}
                      </button>
                    );
                  })}
                </div>
              ) : <Empty text="Aucun affilié pour ce filtre." />}
              <div className="mt-4">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Message personnalisé (consignes, heure de rendez-vous)</span>
                  <button type="button" data-testid="convo-save-template" onClick={saveConvoTemplate}
                    className="text-xs font-bold text-primary hover:underline flex items-center gap-1">
                    <Save size={13} /> Enregistrer par défaut
                  </button>
                </div>
                <textarea data-testid="convo-message" value={convoMessage} onChange={(e) => setConvoMessage(e.target.value)}
                  rows={3} placeholder="Ex. Rendez-vous 30 min avant le coup d'envoi. Prévoir maillot blanc et gourde."
                  className="w-full rounded-lg bg-secondary/40 border border-border p-3 text-sm resize-y focus:outline-none focus:border-primary" />
              </div>
              <div className="mt-4 rounded-lg bg-secondary/40 border border-border p-3">
                <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Canal d'envoi</span>
                <div className="flex flex-wrap gap-4 mt-2">
                  <label data-testid="convo-channel-app" className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input type="checkbox" className="accent-primary w-4 h-4" checked={convoChannels.app}
                      onChange={(e) => setConvoChannels((c) => ({ ...c, app: e.target.checked }))} />
                    <span className="flex items-center gap-1.5"><Bell size={14} className="text-primary" /> Dans l'application</span>
                  </label>
                  <label data-testid="convo-channel-email" className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input type="checkbox" className="accent-primary w-4 h-4" checked={convoChannels.email}
                      onChange={(e) => setConvoChannels((c) => ({ ...c, email: e.target.checked }))} />
                    <span className="flex items-center gap-1.5"><Mail size={14} /> Par email</span>
                  </label>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <Button type="button" data-testid="convo-preview-btn" variant="outline" onClick={openConvoPreview} disabled={convoPreviewLoading}>
                  <Eye size={15} /> {convoPreviewLoading ? "..." : "Aperçu"}
                </Button>
                <Button data-testid="convo-send-btn" className="flex-1" onClick={sendConvoke} disabled={convoSending || !convoSelected.size}>
                  <Mail size={15} /> {convoSending ? "Envoi..." : `Envoyer la convocation (${convoSelected.size})`}
                </Button>
              </div>
            </div>
          );
        })()}
      </Modal>

      <Modal open={!!convoPreview} onClose={() => setConvoPreview(null)} title="Aperçu de la convocation">
        {convoPreview && (
          <div data-testid="convo-preview">
            <p className="text-xs text-muted-foreground mb-3">
              <span className="font-bold uppercase tracking-widest">Objet :</span> {convoPreview.subject}
            </p>
            <iframe title="Aperçu email convocation" data-testid="convo-preview-frame" srcDoc={convoPreview.html}
              className="w-full rounded-lg border border-border bg-white" style={{ height: "60vh" }} />
            <p className="text-[11px] text-muted-foreground mt-2">
              Le prénom réel de chaque affilié sera inséré automatiquement lors de l'envoi.
            </p>
          </div>
        )}
      </Modal>

      <Modal open={!!trackEvent} onClose={() => setTrackEvent(null)} title={`Suivi des convocations — ${trackEvent?.title || ""}`}>
        {!trackData ? <Empty text="Chargement..." /> : (
          <div data-testid="convo-tracking">
            {trackData.summary.total === 0 ? (
              <Empty text="Aucun affilié n'a encore été convoqué pour cet événement." />
            ) : (
              <>
                <div className="flex justify-end mb-3">
                  <Button data-testid="export-convo-tracking-pdf" variant="outline"
                    onClick={() => downloadFile(`/events/${trackEvent.id}/convocations.pdf`, `suivi_convocations_${trackEvent.id}.pdf`).catch(() => toast.error("Export échoué"))}>
                    <FileText size={15} /> Export PDF
                  </Button>
                </div>
                <div className="grid grid-cols-4 gap-2 mb-4">
                  <div data-testid="track-total" className="rounded-lg bg-secondary/50 border border-border p-2 text-center">
                    <p className="text-lg font-bold">{trackData.summary.total}</p>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Convoqués</p>
                  </div>
                  <div data-testid="track-present" className="rounded-lg bg-primary/10 border border-primary/40 p-2 text-center">
                    <p className="text-lg font-bold text-primary">{trackData.summary.present}</p>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Présents</p>
                  </div>
                  <div data-testid="track-absent" className="rounded-lg bg-destructive/10 border border-destructive/40 p-2 text-center">
                    <p className="text-lg font-bold text-destructive">{trackData.summary.absent}</p>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Absents</p>
                  </div>
                  <div data-testid="track-pending" className="rounded-lg bg-yellow-500/10 border border-yellow-500/40 p-2 text-center">
                    <p className="text-lg font-bold text-yellow-500">{trackData.summary.pending}</p>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Sans réponse</p>
                  </div>
                </div>
                <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                  {trackData.convocations.map((c) => (
                    <div key={c.member_id} data-testid={`track-row-${c.member_id}`}
                      className="flex items-center justify-between gap-3 p-3 rounded-lg bg-secondary/40 border border-border">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{c.member_name}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {c.category || "—"}{c.declared_by ? ` · réponse par ${c.declared_by}` : ""}
                        </p>
                      </div>
                      {c.declared === "present" ? (
                        <Badge color="green"><span className="flex items-center gap-1"><CheckCircle2 size={11} /> Présent</span></Badge>
                      ) : c.declared === "absent" ? (
                        <Badge color="red"><span className="flex items-center gap-1"><X size={11} /> Absent</span></Badge>
                      ) : (
                        <Badge color="yellow"><span className="flex items-center gap-1"><Hourglass size={11} /> Sans réponse</span></Badge>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </Modal>


      <Modal open={!!breakdown} onClose={() => setBreakdown(null)} title={breakdown ? `Détail — ${breakdown.ev.title}` : ""}>
        {breakdown && (!breakdown.data ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Chargement…</p>
        ) : (
          <div data-testid="breakdown-modal">
            <div className="flex gap-1.5 flex-wrap mb-4">
              {BREAKDOWN_ORDER.map((k) => {
                const meta = BREAKDOWN_META[k]; const n = (breakdown.data[k] || []).length;
                const active = breakdown.cat === k;
                return (
                  <button key={k} data-testid={`breakdown-tab-${k}`} onClick={() => setBreakdown({ ...breakdown, cat: k })}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                    <meta.icon size={13} /> {meta.label} ({n})
                  </button>
                );
              })}
            </div>
            {(() => {
              const list = breakdown.data[breakdown.cat] || [];
              const meta = BREAKDOWN_META[breakdown.cat];
              if (!list.length) return <Empty text={`Aucun joueur · ${meta.label}`} />;
              return (
                <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
                  {list.map((m) => (
                    <div key={m.member_id} data-testid={`breakdown-row-${m.member_id}`}
                      className="flex items-center justify-between gap-3 p-3 rounded-lg bg-secondary/40 border border-border">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{m.name}</p>
                        <p className="text-[11px] text-muted-foreground">{m.category || "—"}{m.declared_by ? ` · réponse par ${m.declared_by}` : ""}</p>
                      </div>
                      {(isAdmin || isCoach) ? (
                        <div className="flex items-center gap-1 shrink-0">
                          {m.essai && <Badge color="purple"><span className="flex items-center gap-1"><FlaskConical size={11} /> Essai</span></Badge>}
                          <button data-testid={`breakdown-set-present-${m.member_id}`} onClick={() => setDeclare(m.member_id, "present")} title="Marquer prévu"
                            className={`inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors ${m.declared === "present" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-primary"}`}><UserCheck size={14} /></button>
                          <button data-testid={`breakdown-set-absent-${m.member_id}`} onClick={() => setDeclare(m.member_id, "absent")} title="Marquer absent"
                            className={`inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors ${m.declared === "absent" ? "bg-red-500 text-white" : "bg-secondary text-muted-foreground hover:text-red-500"}`}><X size={14} /></button>
                          <button data-testid={`breakdown-set-pending-${m.member_id}`} onClick={() => setDeclare(m.member_id, "pending")} title="Remettre en attente"
                            className={`inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors ${!m.declared ? "bg-yellow-500 text-white" : "bg-secondary text-muted-foreground hover:text-yellow-500"}`}><Hourglass size={14} /></button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 shrink-0">
                          {m.essai && <Badge color="purple"><span className="flex items-center gap-1"><FlaskConical size={11} /> Essai</span></Badge>}
                          <Badge color={meta.color}><span className="flex items-center gap-1"><meta.icon size={11} /> {meta.label}</span></Badge>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        ))}
      </Modal>

      {stageEvent && <StageManager stage={stageEvent} open={stageOpen} onClose={() => setStageOpen(false)} />}
      {campEvent && <CampModal eventId={campEvent.id} title={campEvent.title} onClose={() => { setCampEvent(null); load(); }} />}

      <Modal open={importOpen} onClose={() => setImportOpen(false)} title="Importer le planning depuis Google Sheet">
        <form onSubmit={doImport}>
          <p className="text-sm text-muted-foreground mb-4">
            Collez le lien d'une feuille Google Sheets <b>publique</b> (Partage → « Toute personne disposant du lien peut consulter »).
          </p>
          <Field label="Lien Google Sheet">
            <Input data-testid="import-planning-url-input" required type="url" value={importUrl}
              onChange={(e) => setImportUrl(e.target.value)}
              placeholder="https://docs.google.com/spreadsheets/d/.../edit#gid=0" />
          </Field>
          <div className="rounded-lg bg-secondary/60 border border-border p-3 mb-4">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Colonnes reconnues</p>
            <p className="text-xs text-muted-foreground">Type (entraînement / match / stage / événement), Titre, Date, Date fin, Heure, Lieu, Catégorie, Adversaire, Description. La 1ʳᵉ ligne doit contenir les en-têtes ; Titre et Date sont obligatoires.</p>
            <button type="button" data-testid="download-planning-template-btn"
              onClick={() => downloadFile("/events/import-template.csv", "modele_import_planning.csv").catch(() => toast.error("Téléchargement échoué"))}
              className="mt-2 text-xs font-bold text-primary hover:underline flex items-center gap-1">
              <FileText size={13} /> Télécharger le modèle (CSV)
            </button>
          </div>
          <Button data-testid="import-planning-submit-btn" className="w-full mt-2" disabled={importing}>
            <Upload size={16} /> {importing ? "Import en cours..." : "Importer le planning"}
          </Button>
        </form>
      </Modal>

      <Modal open={vacModal} onClose={() => setVacModal(false)} title="Vacances scolaires (information)">
        <p className="text-sm text-muted-foreground mb-4">
          Les jours fériés légaux belges sont générés automatiquement. Ajoutez ici les périodes de vacances scolaires (Fédération Wallonie-Bruxelles). Elles s'affichent en information sur le calendrier, <b>sans créer d'événement</b> ni impacter les présences.
        </p>
        <form onSubmit={saveVac} className="rounded-lg bg-secondary/40 border border-border p-3 mb-4">
          <Field label="Nom de la période">
            <Input data-testid="vac-name" required value={vacForm.name} onChange={(e) => setVacForm({ ...vacForm, name: e.target.value })}
              placeholder="Ex. Vacances de détente (Carnaval)" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Du"><Input data-testid="vac-start" required type="date" value={vacForm.start} onChange={(e) => setVacForm({ ...vacForm, start: e.target.value })} /></Field>
            <Field label="Au"><Input data-testid="vac-end" type="date" value={vacForm.end} onChange={(e) => setVacForm({ ...vacForm, end: e.target.value })} /></Field>
          </div>
          <div className="flex gap-2 mt-2">
            <Button data-testid="vac-save-btn" className="flex-1" type="submit">{vacEditing ? "Mettre à jour" : "Ajouter la période"}</Button>
            {vacEditing && <Button type="button" variant="outline" onClick={() => { setVacEditing(null); setVacForm({ name: "", start: "", end: "" }); }}>Annuler</Button>}
          </div>
        </form>
        <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1" data-testid="vac-list">
          {allVacs.length ? allVacs.map((v) => (
            <div key={v.id} data-testid={`vac-item-${v.id}`} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-secondary/40 border border-border">
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{v.name}</p>
                <p className="text-xs text-muted-foreground">{fmtDate(v.start)}{v.end && v.end !== v.start ? ` → ${fmtDate(v.end)}` : ""}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button type="button" data-testid={`vac-edit-${v.id}`} onClick={() => editVac(v)} className="h-8 w-8 flex items-center justify-center rounded-lg bg-secondary text-muted-foreground hover:text-primary transition-colors"><Pencil size={14} /></button>
                <button type="button" data-testid={`vac-delete-${v.id}`} onClick={() => removeVac(v.id)} className="h-8 w-8 flex items-center justify-center rounded-lg bg-secondary text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={14} /></button>
              </div>
            </div>
          )) : <Empty text="Aucune période enregistrée." />}
        </div>
      </Modal>
    </div>
  );
}


const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const DOT = { blue: "bg-sky-500", green: "bg-primary", purple: "bg-purple-500", yellow: "bg-yellow-500" };
const CalendarView = ({ month, setMonth, events, holidays = [], vacations = [], birthdays = [], onDayClick }) => {
  const y = month.getFullYear(), m = month.getMonth();
  const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const holidayMap = {};
  holidays.forEach((h) => { holidayMap[h.date] = h.name; });
  const birthdayMap = {};
  birthdays.forEach((b) => { (birthdayMap[b.md] = birthdayMap[b.md] || []).push(b.name); });
  const vacationOf = (k) => vacations.find((v) => v.start <= k && (v.end || v.start) >= k) || null;
  const byDay = {};
  events.forEach((ev) => {
    const add = (k) => { (byDay[k] = byDay[k] || []).push(ev); };
    if (ev.type === "stage" && ev.end_date) {
      let d = new Date(ev.date + "T00:00:00"); const end = new Date(ev.end_date + "T00:00:00"); let guard = 0;
      while (d <= end && guard < 60) { add(dayKey(d)); d.setDate(d.getDate() + 1); guard++; }
    } else if (ev.date) add(ev.date);
  });
  const first = new Date(y, m, 1);
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(y, m, 1 - offset);
  const days = Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  const todayKey = dayKey(new Date());
  const label = month.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  const mStart = dayKey(new Date(y, m, 1));
  const mEnd = dayKey(new Date(y, m + 1, 0));
  const monthEvents = events.filter((ev) => {
    const s = ev.date || "";
    const e = ev.end_date || ev.date || "";
    return s <= mEnd && e >= mStart;
  });
  return (
    <div data-testid="calendar-view">
      <div className="flex items-center justify-between mb-4">
        <button data-testid="cal-prev" onClick={() => setMonth(new Date(y, m - 1, 1))} className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center hover:text-primary transition-colors"><ChevronLeft size={18} /></button>
        <p className="font-display text-lg font-bold uppercase tracking-tight capitalize">{label}</p>
        <button data-testid="cal-next" onClick={() => setMonth(new Date(y, m + 1, 1))} className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center hover:text-primary transition-colors"><ChevronRight size={18} /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map((w) => <div key={w} className="text-center text-[11px] font-bold uppercase tracking-widest text-muted-foreground py-1">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          const inMonth = d.getMonth() === m;
          const k = dayKey(d);
          const evs = byDay[k] || [];
          const clickable = evs.length > 0;
          const holiday = holidayMap[k];
          const vac = vacationOf(k);
          const bdays = birthdayMap[k.slice(5)] || [];
          const infoBg = holiday ? "bg-rose-500/10 border-rose-500/30" : vac ? "bg-amber-500/10 border-amber-500/25" : "";
          return (
            <button key={i} data-testid={clickable ? `cal-day-${k}` : undefined} disabled={!clickable}
              onClick={clickable ? () => onDayClick(k, evs) : undefined}
              title={bdays.length ? `Anniversaire : ${bdays.join(", ")}` : holiday ? `Férié : ${holiday}` : vac ? vac.name : undefined}
              className={`relative min-h-[54px] md:min-h-[84px] rounded-lg border p-1 md:p-1.5 text-left flex flex-col gap-1 transition-colors ${infoBg || (inMonth ? "bg-card border-border" : "bg-secondary/20 border-transparent")} ${clickable ? "hover:border-primary cursor-pointer" : "cursor-default"}`}>
              <div className="flex items-center gap-1">
                <span className={`text-xs font-bold ${k === todayKey ? "w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center" : inMonth ? "" : "text-muted-foreground/40"}`}>{d.getDate()}</span>
                {holiday && <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0" title={holiday} />}
                {!holiday && vac && <span className="inline-block w-1.5 h-1.5 rounded-sm bg-amber-500 shrink-0" title={vac.name} />}
                {bdays.length > 0 && inMonth && <Cake size={11} className="text-pink-400 shrink-0 ml-auto" data-testid={`bday-${k}`} title={bdays.join(", ")} />}
              </div>
              {(holiday || vac) && (
                <span className={`hidden md:block text-[9px] font-semibold leading-tight truncate ${holiday ? "text-rose-400" : "text-amber-500"}`} data-testid={`day-info-${k}`}>
                  {holiday || vac.name}
                </span>
              )}
              {bdays.length > 0 && inMonth && (
                <span className="hidden md:block text-[9px] font-semibold leading-tight truncate text-pink-400">
                  {bdays.length > 1 ? `${bdays.length} anniversaires` : bdays[0]}
                </span>
              )}
              {/* Mobile: compact colored dots only */}
              {evs.length > 0 && (
                <div className="flex md:hidden flex-wrap gap-1 items-center mt-auto">
                  {evs.slice(0, 4).map((ev) => {
                    const T = TYPES[ev.type] || TYPES.event;
                    return <span key={ev.id} className={`inline-block w-1.5 h-1.5 rounded-full ${DOT[T.color] || "bg-primary"} ${ev.status === "cancelled" ? "opacity-40" : ""}`} />;
                  })}
                  {evs.length > 4 && <span className="text-[9px] text-muted-foreground font-bold leading-none">+{evs.length - 4}</span>}
                </div>
              )}
              {/* Desktop: event chips with text */}
              <div className="hidden md:flex flex-col gap-0.5 overflow-hidden">
                {evs.slice(0, 3).map((ev) => {
                  const T = TYPES[ev.type] || TYPES.event;
                  return (
                    <span key={ev.id} className={`flex items-center gap-1 text-[10px] font-semibold px-1 py-0.5 rounded bg-secondary ${ev.status === "cancelled" ? "line-through opacity-60" : ""}`}>
                      <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${DOT[T.color] || "bg-primary"}`} />
                      <span className="truncate">{ev.time ? ev.time + " " : ""}{ev.title}</span>
                    </span>
                  );
                })}
                {evs.length > 3 && <span className="text-[10px] text-muted-foreground font-bold">+{evs.length - 3}</span>}
              </div>
            </button>
          );
        })}
      </div>

      {/* Mobile: agenda list of the whole month (chronological) */}
      <div className="md:hidden mt-5" data-testid="calendar-agenda">
        <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Agenda du mois</p>
        {monthEvents.length ? (
          <div className="space-y-2">
            {monthEvents.map((ev) => {
              const T = TYPES[ev.type] || TYPES.event;
              return (
                <button key={ev.id} data-testid="agenda-item" onClick={() => onDayClick(ev.date, byDay[ev.date] || [ev])}
                  className="w-full flex items-center gap-3 p-3 rounded-lg bg-card border border-border text-left hover:border-primary/40 transition-colors">
                  <div className="text-center w-10 shrink-0">
                    <p className="font-display text-lg font-black text-primary leading-none">{(ev.date || "").slice(8, 10)}</p>
                    <p className="text-[10px] uppercase text-muted-foreground">{(ev.date || "").slice(5, 7)}</p>
                  </div>
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${DOT[T.color] || "bg-primary"}`} />
                  <div className="min-w-0 flex-1">
                    <p className={`font-semibold text-sm truncate ${ev.status === "cancelled" ? "line-through text-muted-foreground" : ""}`}>{ev.title}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                      <span>{T.label}</span>
                      {ev.time && <span className="flex items-center gap-1"><Clock size={11} /> {ev.time}</span>}
                      {ev.location && <span className="flex items-center gap-1 min-w-0"><MapPin size={11} className="shrink-0" /> <span className="truncate">{ev.location}</span></span>}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        ) : <Empty text="Aucun événement ce mois-ci." />}
      </div>
    </div>
  );
};
